import { useUsageBreakdown } from '../hooks/queries';
import { useLanguage, useT } from '../i18n';

const SHOWN = 4;

/**
 * This month's usage in the account menu: the budget used (when the admin set one) and one
 * stacked bar of the models, by cost (or by tokens when nothing was billed), with a legend.
 */
export function UsageBreakdownPanel({ enabled }: { enabled: boolean }) {
  const t = useT();
  const language = useLanguage();
  const { data } = useUsageBreakdown(enabled);
  if (!data) return null;
  const locale = language === 'en' ? 'en-GB' : `${language}-BE`;
  const money = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(value);
  const compact = (value: number) =>
    new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  const byCost = data.models.some((model) => model.costUsd > 0);
  const amount = (model: (typeof data.models)[number]) =>
    byCost ? model.costUsd : model.tokensIn + model.tokensOut;
  const total = data.models.reduce((sum, model) => sum + amount(model), 0);
  const rows = data.models
    .slice(0, SHOWN)
    .map((model) => ({ name: model.model, value: amount(model) }));
  const rest = data.models.slice(SHOWN).reduce((sum, model) => sum + amount(model), 0);
  if (rest > 0) rows.push({ name: t('usageOther'), value: rest });
  const requests = data.models.reduce((sum, model) => sum + model.requests, 0);
  const tokens = data.models.reduce((sum, model) => sum + model.tokensIn + model.tokensOut, 0);
  const format = (value: number) => (byCost ? money(value) : `${compact(value)} tokens`);

  return (
    <section className="nv-usage" aria-label={t('usageThisMonth')}>
      <div className="nv-usage-head">
        <span>{t('usageThisMonth')}</span>
        <span className="tabular-nums">
          {data.budgetUsd !== null
            ? `${money(data.spentUsd)} ${t('usageBudgetOf')} ${money(data.budgetUsd)}`
            : byCost
              ? money(data.spentUsd)
              : ''}
        </span>
      </div>
      {data.budgetUsd !== null && (
        <meter
          className="nv-meter"
          aria-label={t('usageThisMonth')}
          min={0}
          max={data.budgetUsd}
          value={Math.min(data.spentUsd, data.budgetUsd)}
        />
      )}
      {total === 0 ? (
        <p className="nv-usage-none">{t('usageNone')}</p>
      ) : (
        <>
          <div className="nv-usage-bar" aria-hidden="true">
            {rows.map((row, index) => (
              <span
                key={row.name}
                className={`nv-usage-c${index}`}
                style={{ width: `${(row.value / total) * 100}%` }}
              />
            ))}
          </div>
          <ul className="nv-usage-legend">
            {rows.map((row, index) => (
              <li key={row.name}>
                <span className={`nv-usage-dot nv-usage-c${index}`} aria-hidden="true" />
                <span className="nv-usage-name" title={row.name}>
                  {row.name}
                </span>
                <span className="tabular-nums">{format(row.value)}</span>
              </li>
            ))}
          </ul>
          <p className="nv-usage-none tabular-nums">
            {compact(requests)} {t('usageRequests')} · {compact(tokens)} tokens
          </p>
        </>
      )}
    </section>
  );
}
